import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Webcam from "react-webcam";
import API_BASE_URL from "../config";
import "./AttendanceManagement.css";

// React Icons Imports
import {
  FaClock,
  FaUserCircle,
  FaMapMarkerAlt,
  FaMap,
  FaGlobe,
  FaExternalLinkAlt,
  FaCamera,
  FaPowerOff,
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
  FaExclamationCircle,
  FaCalendarAlt,
  FaChartBar,
  FaHistory,
  FaCalendar,
  FaCalendarDay,
  FaCalendarWeek,
  FaCalendarCheck,
  FaEye,
  FaImage,
  FaLocationArrow,
  FaSpinner,
  FaTimes,
  FaUser,
  FaMapPin,
  FaDotCircle,
  FaCircle,
  FaArrowRight,
  FaArrowLeft
} from 'react-icons/fa';

const AttendanceManagement = ({ employee, assignedEmployees, managerView }) => {
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapType, setMapType] = useState("interactive");
  const [activeTab, setActiveTab] = useState("today");
  const [cameraActive, setCameraActive] = useState(false);

  const webcamRef = useRef(null);
  const statusRef = useRef(null);

  // Get location & fetch history
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => console.error("Error fetching location:", err)
      );
    }

    if (managerView) {
      fetchTeamAttendance();
    } else {
      fetchAttendanceHistory();
    }
  }, []);

  // Animation for status messages
  useEffect(() => {
    if (attendanceStatus && statusRef.current) {
      statusRef.current.classList.add("attendance-status-visible");
      const timer = setTimeout(() => {
        if (statusRef.current) {
          statusRef.current.classList.remove("attendance-status-visible");
        }
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [attendanceStatus]);

  // Optimize image capture and compression
  const captureImage = async () => {
    if (!webcamRef.current) {
      return null;
    }

    try {
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) return null;

      // Compress image before sending
      const compressedImage = await compressImage(imageSrc, 0.7); // 70% quality
      return compressedImage;
    } catch (error) {
      console.error("Error capturing image:", error);
      return null;
    }
  };

  // Image compression function
  const compressImage = (base64Data, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set maximum dimensions
        const maxWidth = 640;
        const maxHeight = 480;
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions maintaining aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw image on canvas with new dimensions
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to JPEG with specified quality
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        
        // Remove data URL prefix and return only base64
        const base64WithoutPrefix = compressedBase64.replace(/^data:image\/jpeg;base64,/, '');
        
        // Log image size for debugging
        const sizeKB = Math.round((base64WithoutPrefix.length * 3) / 4 / 1024);
        console.log(`Compressed image size: ${sizeKB} KB`);
        
        resolve(base64WithoutPrefix);
      };
      
      img.onerror = reject;
      img.src = base64Data;
    });
  };

  const handlePunch = async (type) => {
    try {
      setLoading(true);
      
      if (!cameraActive) {
        setAttendanceStatus("Please enable camera first");
        setLoading(false);
        return;
      }

      const base64Photo = await captureImage();
      if (!base64Photo) {
        setAttendanceStatus("Could not capture image. Please try again.");
        setLoading(false);
        return;
      }

      // Validate location
      if (!location.lat || !location.lng) {
        setAttendanceStatus("Please allow location access");
        setLoading(false);
        return;
      }

      const payload = {
        emp_id: employee.emp_id,
        name: employee.name,
        type,
        location,
        photo: base64Photo,
        contentType: "image/jpeg",
      };

      // Log payload size for debugging
      const payloadSizeKB = Math.round(JSON.stringify(payload).length / 1024);
      console.log(`Payload size: ${payloadSizeKB} KB`);

      const res = await axios.post(`${API_BASE_URL}/api/attendance`, payload, {
        timeout: 30000, // 30 second timeout
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (res.data.success) {
        setAttendanceStatus(`${type} successful!`);
        fetchAttendanceHistory();
      } else {
        setAttendanceStatus(res.data.message || `${type} failed`);
      }
    } catch (err) {
      console.error("Error punching attendance:", err);
      
      if (err.code === 'ECONNABORTED') {
        setAttendanceStatus("Request timeout. Please check your connection.");
      } else if (err.response?.status === 413) {
        setAttendanceStatus("Image too large. Please try again.");
      } else {
        setAttendanceStatus(
          err.response?.data?.message || 
          "Error saving attendance. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceHistory = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/attendance`, {
        params: { emp_id: employee.emp_id },
        timeout: 10000,
      });
      
      if (res.data.success) {
        setAttendanceHistory(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching attendance history:", err);
    }
  };

  const fetchTeamAttendance = async () => {
    if (!assignedEmployees || assignedEmployees.length === 0) return;

    try {
      const token = sessionStorage.getItem("managerToken");
      const res = await axios.get(`${API_BASE_URL}/api/attendance/team`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          date: new Date().toISOString().split("T")[0],
          managerId: employee.emp_id 
        },
        timeout: 10000,
      });

      if (res.data.success && res.data.teamAttendance) {
        const filtered = res.data.teamAttendance.filter((att) =>
          assignedEmployees.some((emp) => emp.emp_id === att.employee.emp_id)
        );
        setAttendanceHistory(filtered);
      }
    } catch (err) {
      console.error("Error fetching team attendance:", err);
    }
  };

  const handleViewOnMap = (punchRecord, type, employeeName) => {
    if (punchRecord.location) {
      setSelectedLocation({
        lat: punchRecord.location.lat,
        lng: punchRecord.location.lng,
        type: type,
        timestamp: punchRecord.timestamp,
        employee: employeeName || employee.name
      });
      setShowMap(true);
    }
  };

  const renderStaticMap = (lat, lng) => {
    return `https://staticmap.openstreetmap.de/staticmap.php?
      center=${lat},${lng}&
      zoom=15&
      size=600x300&
      markers=${lat},${lng},red-pushpin&
      base=default`;
  };

  const renderInteractiveMap = (lat, lng) => {
    return (
      <iframe
        width="100%"
        height="300"
        frameBorder="0"
        scrolling="no"
        marginHeight="0"
        marginWidth="0"
        src={`https://www.openstreetmap.org/export/embed.html?
          bbox=${lng-0.01},${lat-0.01},${lng+0.01},${lat+0.01}&
          layer=mapnik&
          marker=${lat},${lng}`}
        className="attendance-interactive-map"
        title="Location Map"
      />
    );
  };

  const LocationPin = ({ location, type, employeeName }) => (
    <div 
      className="attendance-location-pin" 
      onClick={() => handleViewOnMap({ location, timestamp: new Date() }, type, employeeName)}
    >
      <div className="attendance-pin-icon">
        <FaMapPin />
      </div>
      <span className="attendance-location-tooltip">
        {type === "punchIn" ? "Punch In" : "Punch Out"} Location
        <br />
        {location.lat?.toFixed(6)}, {location.lng?.toFixed(6)}
        <br />
        <small>Click to view on map</small>
      </span>
    </div>
  );

  const PulseLoader = () => (
    <div className="attendance-pulse-loader">
      <div className="attendance-pulse-dot"></div>
      <div className="attendance-pulse-dot"></div>
      <div className="attendance-pulse-dot"></div>
    </div>
  );

  return (
    <div className="attendance-management-container">
      {/* Animated Background */}
      <div className="attendance-animated-bg">
        {/* <div className="attendance-floating-shapes">
          <div className="attendance-shape shape-1"></div>
          <div className="attendance-shape shape-2"></div>
          <div className="attendance-shape shape-3"></div>
          <div className="attendance-shape shape-4"></div>
        </div> */}
      </div>

      <div className="attendance-content-wrapper">
        <div className="attendance-header-section">
          <h2 className="attendance-main-title">
            <span className="attendance-title-icon">
              <FaClock />
            </span>
            Attendance Management
            <div className="attendance-title-underline"></div>
          </h2>

          {!managerView && (
            <div className="attendance-employee-card">
              <div className="attendance-employee-avatar">
                {employee.name?.charAt(0).toUpperCase()}
              </div>
              <div className="attendance-employee-info">
                <h3>{employee.name}</h3>
                <p>ID: {employee.emp_id}</p>
                <div className="attendance-status-indicator">
                  <div className="attendance-status-dot"></div>
                  <span>Ready to Punch</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {!managerView && (
          <div className="attendance-camera-section">
            <div className="attendance-camera-header">
              <h4>Identity Verification</h4>
              <button 
                className={`attendance-camera-toggle ${cameraActive ? 'active' : ''}`}
                onClick={() => setCameraActive(!cameraActive)}
                type="button"
              >
                {cameraActive ? (
                  <>
                    <FaCamera /> Camera On
                  </>
                ) : (
                  <>
                    <FaPowerOff /> Camera Off
                  </>
                )}
              </button>
            </div>
            
            <div className={`attendance-webcam-container ${cameraActive ? 'active' : ''}`}>
              {cameraActive ? (
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  screenshotFormat="image/jpeg"
                  width={320}
                  height={240}
                  videoConstraints={{ 
                    facingMode: "user",
                    width: { ideal: 640 },
                    height: { ideal: 480 }
                  }}
                  className="attendance-webcam-feed"
                  mirrored={true}
                />
              ) : (
                <div className="attendance-camera-placeholder">
                  <div className="attendance-camera-icon">
                    <FaCamera />
                  </div>
                  <p>Camera is disabled</p>
                  <button 
                    className="attendance-enable-camera-btn"
                    onClick={() => setCameraActive(true)}
                    type="button"
                  >
                    <FaCamera /> Enable Camera
                  </button>
                </div>
              )}
            </div>
            
            {cameraActive && (
              <div className="attendance-camera-instructions">
                <small>
                  <FaInfoCircle /> Make sure your face is clearly visible in the frame
                </small>
              </div>
            )}
          </div>
        )}

        {!managerView && (
          <div className="attendance-actions-section">
            <div className="attendance-action-buttons">
              <button 
                className={`attendance-punch-btn punch-in ${loading ? 'loading' : ''}`}
                onClick={() => handlePunch("Punch In")} 
                disabled={loading || !cameraActive}
                type="button"
              >
                {loading ? <PulseLoader /> : (
                  <>
                    <span className="attendance-btn-icon">
                      <FaDotCircle />
                    </span>
                    Punch In
                  </>
                )}
              </button>
              <button 
                className={`attendance-punch-btn punch-out ${loading ? 'loading' : ''}`}
                onClick={() => handlePunch("Punch Out")} 
                disabled={loading || !cameraActive}
                type="button"
              >
                {loading ? <PulseLoader /> : (
                  <>
                    <span className="attendance-btn-icon">
                      <FaCircle />
                    </span>
                    Punch Out
                  </>
                )}
              </button>
            </div>
            <div className="attendance-actions-info">
              <small>
                {cameraActive 
                  ? (
                    <>
                      <FaCamera /> Camera active - Click button to punch
                    </>
                  ) 
                  : (
                    <>
                      <FaExclamationCircle /> Enable camera first to punch attendance
                    </>
                  )}
              </small>
            </div>
          </div>
        )}

        {attendanceStatus && (
          <div 
            ref={statusRef}
            className={`attendance-status-message ${
              attendanceStatus.includes('successful') ? 'success' : 
              attendanceStatus.includes('failed') || attendanceStatus.includes('Error') ? 'error' : 'info'
            }`}
          >
            {attendanceStatus.includes('successful') ? (
              <FaCheckCircle />
            ) : attendanceStatus.includes('failed') || attendanceStatus.includes('Error') ? (
              <FaTimesCircle />
            ) : (
              <FaInfoCircle />
            )}
            {attendanceStatus}
          </div>
        )}

        {!managerView && location.lat && location.lng && (
          <div className="attendance-location-card">
            <div className="attendance-location-header">
              <span className="attendance-location-icon">
                <FaMapMarkerAlt />
              </span>
              <h4>Current Location</h4>
            </div>
            <div className="attendance-location-coordinates">
              <code>{location.lat.toFixed(6)}, {location.lng.toFixed(6)}</code>
            </div>
            <div className="attendance-location-accuracy">
              <FaLocationArrow /> Location accuracy: ~{Math.random() * 10 + 5}m
            </div>
          </div>
        )}

        {/* Map Modal */}
        {showMap && selectedLocation && (
          <div className="attendance-map-modal">
            <div className="attendance-map-modal-content">
              <div className="attendance-map-modal-header">
                <h3>
                  <span className="attendance-map-employee-icon">
                    <FaUser />
                  </span>
                  {selectedLocation.employee} - 
                  <span className={`attendance-map-punch-type ${selectedLocation.type}`}>
                    {selectedLocation.type === "punchIn" ? "Punch In" : "Punch Out"}
                  </span>
                </h3>
                <button 
                  className="attendance-map-close-btn"
                  onClick={() => setShowMap(false)}
                  type="button"
                >
                  <FaTimes />
                </button>
              </div>
              
              <div className="attendance-map-type-selector">
                <button 
                  className={`attendance-map-type-btn ${mapType === "static" ? "active" : ""}`}
                  onClick={() => setMapType("static")}
                  type="button"
                >
                  <FaMap /> Static Map
                </button>
                <button 
                  className={`attendance-map-type-btn ${mapType === "interactive" ? "active" : ""}`}
                  onClick={() => setMapType("interactive")}
                  type="button"
                >
                  <FaGlobe /> Interactive Map
                </button>
              </div>

              <div className="attendance-map-container">
                {mapType === "static" ? (
                  <img 
                    src={renderStaticMap(selectedLocation.lat, selectedLocation.lng)} 
                    alt="Location Map"
                    className="attendance-static-map"
                    onError={(e) => {
                      console.error("Failed to load static map");
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  renderInteractiveMap(selectedLocation.lat, selectedLocation.lng)
                )}
              </div>

              <div className="attendance-location-details">
                <div className="attendance-detail-item">
                  <strong>
                    <FaMapMarkerAlt /> Coordinates:
                  </strong> 
                  <code>{selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}</code>
                </div>
                <div className="attendance-detail-item">
                  <strong>
                    <FaClock /> Time:
                  </strong> 
                  {new Date(selectedLocation.timestamp).toLocaleString()}
                </div>
                <div className="attendance-external-links">
                  <a 
                    href={`https://www.openstreetmap.org/?mlat=${selectedLocation.lat}&mlon=${selectedLocation.lng}#map=15/${selectedLocation.lat}/${selectedLocation.lng}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="attendance-external-link"
                  >
                    <FaExternalLinkAlt /> OpenStreetMap
                  </a>
                  <a 
                    href={`https://maps.google.com/?q=${selectedLocation.lat},${selectedLocation.lng}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="attendance-external-link"
                  >
                    <FaExternalLinkAlt /> Google Maps
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="attendance-history-section">
          <div className="attendance-history-header">
            <h3>
              <span className="attendance-history-icon">
                <FaChartBar />
              </span>
              {managerView ? "Team Attendance" : "Your Attendance History"}
            </h3>
            <div className="attendance-history-tabs">
              <button 
                className={`attendance-tab-btn ${activeTab === "today" ? "active" : ""}`}
                onClick={() => setActiveTab("today")}
                type="button"
              >
                <FaCalendarDay /> Today
              </button>
              <button 
                className={`attendance-tab-btn ${activeTab === "week" ? "active" : ""}`}
                onClick={() => setActiveTab("week")}
                type="button"
              >
                <FaCalendarWeek /> This Week
              </button>
              <button 
                className={`attendance-tab-btn ${activeTab === "month" ? "active" : ""}`}
                onClick={() => setActiveTab("month")}
                type="button"
              >
                <FaCalendarAlt /> This Month
              </button>
            </div>
          </div>

          {attendanceHistory.length === 0 ? (
            <div className="attendance-empty-state">
              <div className="attendance-empty-icon">
                <FaCalendarAlt />
              </div>
              <h4>No attendance records found</h4>
              <p>Your attendance history will appear here once you start punching in/out.</p>
            </div>
          ) : (
            <div className="attendance-table-container">
              <table className="attendance-data-table">
                <thead>
                  <tr>
                    {!managerView && <th>Date</th>}
                    <th>Employee</th>
                    <th>Status</th>
                    <th>Punch In</th>
                    <th>Punch Out</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceHistory.map((record, idx) => {
                    const employeeName = managerView ? record.employee?.name : employee.name;
                    return (
                      <tr key={idx} className="attendance-table-row">
                        {!managerView && (
                          <td className="attendance-date-cell">
                            <div className="attendance-date-wrapper">
                              <span className="attendance-date-day">
                                {new Date(record.date).getDate()}
                              </span>
                              <span className="attendance-date-month">
                                {new Date(record.date).toLocaleString('default', { month: 'short' })}
                              </span>
                            </div>
                          </td>
                        )}
                        <td className="attendance-employee-cell">
                          <div className="attendance-employee-badge">
                            <span className="attendance-employee-avatar-small">
                              {employeeName?.charAt(0).toUpperCase()}
                            </span>
                            {employeeName}
                          </div>
                        </td>
                        <td className="attendance-status-cell">
                          <span className={`attendance-status-badge ${record.status?.toLowerCase() || 'pending'}`}>
                            {managerView ? record.status : "--"}
                          </span>
                        </td>

                        <td className="attendance-punch-cell">
                          {record.punchIn?.timestamp ? (
                            <div className="attendance-punch-record">
                              <div className="attendance-time-badge in">
                                {new Date(record.punchIn.timestamp).toLocaleTimeString()}
                              </div>
                              {record.punchIn.location && (
                                <div className="attendance-location-section">
                                  <LocationPin 
                                    location={record.punchIn.location} 
                                    type="punchIn" 
                                    employeeName={employeeName}
                                  />
                                </div>
                              )}
                              {record.punchIn.photo && (
                                <div className="attendance-photo-preview">
                                  <img 
                                    src={record.punchIn.photo} 
                                    alt="Punch In" 
                                    className="attendance-punch-photo"
                                  />
                                  <div className="attendance-photo-tooltip">
                                    <FaCamera /> Punch In Photo
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="attendance-no-data">--</span>
                          )}
                        </td>

                        <td className="attendance-punch-cell">
                          {record.punchOut?.timestamp ? (
                            <div className="attendance-punch-record">
                              <div className="attendance-time-badge out">
                                {new Date(record.punchOut.timestamp).toLocaleTimeString()}
                              </div>
                              {record.punchOut.location && (
                                <div className="attendance-location-section">
                                  <LocationPin 
                                    location={record.punchOut.location} 
                                    type="punchOut" 
                                    employeeName={employeeName}
                                  />
                                </div>
                              )}
                              {record.punchOut.photo && (
                                <div className="attendance-photo-preview">
                                  <img 
                                    src={record.punchOut.photo} 
                                    alt="Punch Out" 
                                    className="attendance-punch-photo"
                                  />
                                  <div className="attendance-photo-tooltip">
                                    <FaCamera /> Punch Out Photo
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="attendance-no-data">--</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceManagement;